import cx from 'classnames';
import _ from 'lodash';
import React, { ReactNode, useState } from 'react';
import { Key } from 'w3c-keys';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import styles from './input-base.module.scss';

export type InputBaseValue = string | number | undefined;
export type InputBaseType = 'text' | 'password' | 'email' | 'number';

export enum InputVariant {
  DEFAULT = 'default',
  NUMBER = 'number',
}

export interface InputBaseComponentProps {
  /**
   * Input value
   */
  value?: InputBaseValue;

  /**
   * input container class name
   */
  className?: string;

  /**
   * Input id
   */
  id?: string;

  /**
   * input type
   * @default text
   */
  type?: InputBaseType;

  /**
   * if on gray background the background color of the input
   * changes to a lighter shade.
   * @default false
   */
  onGrayBackground?: boolean;

  /**
   * Suffix component
   */
  suffix?: ReactNode;

  /**
   * Prefix component
   */
  prefix?: ReactNode;

  /**
   * show error state
   * @default false
   */
  hasError?: boolean;

  /**
   * show warning state
   * @default false
   */
  hasWarning?: boolean;

  /**
   * error message/text
   */
  helperText?: string;

  /**
   * show verified state
   */
  isVerified?: boolean;

  /**
   * The callback function that is triggered when Enter key is pressed
   */
  onEnter?: (evt: React.KeyboardEvent) => void;

  /**
   * The callback function that is triggered when tab key is pressed
   */
  onTab?: (evt: React.KeyboardEvent) => void;

  /**
   * The callback function that is triggered when space key is pressed
   */
  onSpace?: (evt: React.KeyboardEvent) => void;

  /**
   * The callback function that is triggered when escape key is pressed
   */
  onEscape?: (evt: React.KeyboardEvent) => void;

  /**
   * The callback function that is triggered when user  input
   */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Input ref
   */
  inputRef?: React.RefObject<HTMLInputElement>;

  /**
   * Input variant type which can be Default or Number.
   * This is for internal use only and used only from input-number.tsx
   * @default InputVariant.DEFAULT
   */
  variant?: InputVariant;

  /**
   * True if Custom Formatter is Present else false.
   * This is for internal use only and used only from input-number.tsx
   * @default false
   */
  isFormatterPresent?: boolean;

  /**
   * Helper text ref
   */
  helperRef?: React.RefObject<HTMLInputElement>;
}

export type InputBaseProps = InputBaseComponentProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size' | 'type' | 'onChange'>;

export const InputBase: React.FC<InputBaseProps> = ({
  className,
  name,
  onEnter,
  onTab,
  onSpace,
  onEscape,
  onKeyDown,
  onFocus,
  onBlur,
  value = '',
  onChange,
  helperText,
  disabled = false,
  hasError = false,
  hasWarning = false,
  onGrayBackground = false,
  suffix = null,
  prefix = null,
  type = 'text',
  isVerified = false,
  readOnly = false,
  inputRef,
  variant = InputVariant.DEFAULT,
  isFormatterPresent = false,
  helperRef,
  ...restProps
}: InputBaseProps) => {
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const isInputNumberVariant = variant === InputVariant.NUMBER;

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setHasFocus(true);
    if (onFocus) {
      onFocus(event);
    }
  };
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setHasFocus(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: val } = event.target;

    /* If Variant is input-number, then don't allow text as input unless some Formatter is present.
           If Formatter is present then input-number will allow character input
           and the formatting, parsing logic will be handled by input-number as earlier */
    if (isInputNumberVariant && !isFormatterPresent) {
      const isOnlyMinus = val === '-'; // Edge case. Where user enters '-', isNaN check fails. So we need to skip this case.
      // eslint-disable-next-line no-new-wrappers
      if (!isOnlyMinus && _.isNaN(new Number(val))) {
        return;
      }
    }
    // https://github.com/facebook/react/issues/955#issuecomment-469352730
    onChange(event);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === Key.Enter && onEnter) {
      onEnter(event);
    } else if (event.key === Key.Escape && onEscape) {
      onEscape(event);
    } else if (event.key === Key.Tab && onTab) {
      onTab(event);
    } else if (event.key === Key.Space && onSpace) {
      onSpace(event);
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const containerClassNames = cx(className, styles.container, {
    [styles.disabled]: disabled,
    [styles.hasError]: hasError,
    [styles.hasWarning]: hasWarning,
    [styles.hasFocus]: hasFocus,
    [styles.onGrayBackground]: onGrayBackground,
    [styles.readOnly]: readOnly,
  });

  const suffixClassNames = cx(styles.suffix, {
    [styles.inputNumberSuffix]: className?.includes('inputNumber'),
  });

  const getHelpTextMarkup = () => {
    if (helperText) {
      const helperTextClassName = cx(styles.helperText, {
        [styles.hasError]: hasError,
        [styles.hasWarning]: hasWarning,
        [styles.disabled]: disabled,
      });
      return (
        <span className={helperTextClassName} aria-live="assertive" tabIndex={-1} ref={helperRef}>
          {hasError && (
            <span className={styles.iconStyle}>
              <Icon
                color={IconColor.TEXT_COLOR}
                size={IconSize.XSMALL}
                id={IconID.EXCLAMATION_POINT_CIRCLE}
              />
            </span>
          )}
          {isVerified && !hasError && (
            <span className={styles.iconStyle}>
              <Icon color={IconColor.GREEN} size={IconSize.XSMALL} id={IconID.CHECKMARK_CIRCLE} />
            </span>
          )}
          {helperText}
        </span>
      );
    }
    return null;
  };

  return (
    <div data-radiant-v2>
      <span className={containerClassNames}>
        {prefix && <span className={styles.prefix}>{prefix}</span>}
        <input
          disabled={disabled}
          type={type}
          value={value}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={styles.input}
          readOnly={readOnly}
          ref={inputRef}
          {...restProps}
        />
        {suffix && <span className={suffixClassNames}>{suffix}</span>}
      </span>
      {getHelpTextMarkup()}
    </div>
  );
};
